import React, { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../api/axios";
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Fade,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsightsIcon from '@mui/icons-material/Insights';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoIcon from '@mui/icons-material/Info';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';

const UploadPage = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    bank: "",
    fullName: "",
    email: "",
    phone: "",
    file: null,
  });

  const [modifiedFields, setModifiedFields] = useState({
    bank: false,
    fullName: false,
    email: false,
    phone: false
  });

  const [fileName, setFileName] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [userHasMortgage, setUserHasMortgage] = useState(false);
  
  const [originalProfileData, setOriginalProfileData] = useState({
    bank: "",
    fullName: "",
    email: "",
    phone: ""
  });

  const getUserMortgageBank = useCallback(() => {
    if (user?.bank_id) {
      const bankMap = {
        1: "הפועלים",
        2: "לאומי",
        3: "מזרחי",
        4: "דיסקונט",
        5: "בינלאומי",
        6: "אוצר החייל",
        7: "מסד",
        8: "חוץ בנקאי"
      };
      return bankMap[user.bank_id] || "אחר";
    }
    return "";
  }, [user]);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (user && !authLoading) {
      setUserHasMortgage(!!user.has_mortgage_data);
      
      const fullName = user.first_name && user.last_name 
        ? `${user.first_name} ${user.last_name}` 
        : user.first_name || "";
      const email = user.email || "";
      const phone = user.phone || "";
      const bank = getUserMortgageBank() || "";
      
      setOriginalProfileData({
        bank,
        fullName,
        email,
        phone
      });
      
      setModifiedFields({
        bank: false,
        fullName: false,
        email: false,
        phone: false
      });
      
      setFormData(prevData => ({
        ...prevData,
        fullName,
        email,
        phone,
        bank
      }));
    }
  }, [user, authLoading, getUserMortgageBank]);

  useEffect(() => {
    if (user) {
      getUserMortgageBank();
    }
  }, [user, getUserMortgageBank]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    
    if (originalProfileData[name] !== value) {
      setModifiedFields(prev => ({
        ...prev,
        [name]: true
      }));
    } else {
      setModifiedFields(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFormData({ ...formData, file });
    }
  };

  const handleTermsChange = (e) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      setIsError(true);
      setUploadMessage("יש להעלות מסמך קודם!");
      return;
    }

    if (!agreedToTerms) {
      setIsError(true);
      setUploadMessage("יש לאשר את קריאת תקנון השימוש!");
      return;
    }

    setIsLoading(true);
    setUploadMessage("");
    setIsSuccess(false);
    setIsError(false);

    const data = new FormData();
    data.append("bank", formData.bank);
    data.append("fullName", formData.fullName);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("file", formData.file);
    data.append("agreedToTerms", agreedToTerms);

    if (user && user.email) {
      data.append("userEmail", user.email);
      data.append("isAuthenticated", "true");
    }

    try {
      // const response = await fetch("https://mashkanta-me.com/api_test/", );
      const response = await axiosInstance.post(`api_test/`, data);

      
      // בדיקת סטטוס ההצלחה - עם Axios בודקים את קוד הסטטוס
      if (response.status >= 200 && response.status < 300) {
        
        setIsSuccess(true);
        setUploadMessage("הקובץ נשלח בהצלחה! נחזור אליך תוך יום עסקים.");
        // הנתונים מהשרת נמצאים ב-response.data
        console.log("Response data:", response.data);
        
        // איפוס הטופס
        setFormData({
          bank: "",
          fullName: "",
          email: "",
          phone: "",
          file: null,
        });
        setFileName("");
        setAgreedToTerms(false);
      } else {
        setIsError(true);
        setUploadMessage("אירעה שגיאה בשליחת הקובץ. אנא נסה שוב.");
      }
    } catch (err) {
      console.error(err);
      
      // גישה לפרטי השגיאה מהשרת (אם קיימים)
      if (err.response) {
        // השרת החזיר תשובה עם קוד סטטוס שגיאה
        console.error("Error response:", err.response.data);
        console.error("Status code:", err.response.status);
        
        // אם השרת החזיר הודעת שגיאה ספציפית, נשתמש בה
        setUploadMessage(err.response.data?.message || "אירעה שגיאה בשליחת הקובץ. אנא נסה שוב.");
      } else if (err.request) {
        // הבקשה נשלחה אבל לא התקבלה תשובה
        console.error("No response received:", err.request);
        setUploadMessage("לא התקבלה תשובה מהשרת. בדוק את החיבור לאינטרנט.");
      } else {
        // שגיאה בהגדרת הבקשה
        console.error("Request error:", err.message);
        setUploadMessage("אירעה שגיאה בשליחת הקובץ. אנא נסה שוב.");
      }
      
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const banks = [
    "הפועלים",
    "לאומי",
    "מזרחי",
    "דיסקונט",
    "בינלאומי",
    "אוצר החייל",
    "מסד",
    "חוץ בנקאי",
    "אחר"
  ];

  return (
    <Fade in={loaded} timeout={600}>
      <PageContainer>
        <ServiceBadge>
          <WorkspacePremiumIcon sx={{ fontSize: 18, color: "#C9B18C", mr: 0.5 }} />
          <Typography variant="caption" sx={{ letterSpacing: 0.8 }}>שירות VIP</Typography>
        </ServiceBadge>
        
        <PageHeader>
          <Typography variant="h4" component="h1" fontWeight={600}>
            בדיקת מחזור ללא עלות
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            שלח את דוח היתרות לסילוק ונחזור אליך עם המלצות אישיות
          </Typography>
        </PageHeader>
        
        {userHasMortgage && (
          <MortgageAlert>
            <InfoIcon sx={{ color: "#896E3B", fontSize: 20, mr: 1 }} />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                שים לב: יש לך כבר נתוני משכנתא במערכת.
              </Typography>
              <Typography variant="body2">
                העלאת דוח חדש תעדכן את הנתונים הקיימים שלך.
              </Typography>
            </Box>
          </MortgageAlert>
        )}
        
        <MainContent>
          <LeftColumn>
            <InfoCard>
              <InfoCardHeader>
                <DescriptionOutlinedIcon sx={{ color: "#C9B18C", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  מהו דוח יתרות לסילוק?
                </Typography>
              </InfoCardHeader>
              
              <Typography variant="body2" paragraph sx={{ mb: 2, color: "#5E6B7D" }}>
                זהו מסמך רשמי מהבנק שמציג את יתרת המשכנתא שלכם, כולל סכום הקרן, יתרת הריבית, והתחייבויות נוספות. המסמך חיוני לבדיקת אפשרות לשיפור תנאי המשכנתא שלכם.
              </Typography>
              
              <GuideButtonContainer>
                <GuideButton to="/how-to-download" target="_blank">
                  <DescriptionOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
                  לחצו כאן למדריך המלא להורדת הדוח
                  <ArrowIcon>→</ArrowIcon>
                </GuideButton>
              </GuideButtonContainer>
              
              <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.06)' }} />
              
              <BenefitsSection>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: "#2E353E" }}>
                  יתרונות השירות:
                </Typography>
                
                <BenefitItem>
                  <CheckCircleOutlineIcon sx={{ color: "#C9B18C", fontSize: 20 }} />
                  <Typography variant="body2">ניתוח מקצועי של יועצי משכנתאות</Typography>
                </BenefitItem>
                
                <BenefitItem>
                  <InsightsIcon sx={{ color: "#C9B18C", fontSize: 20 }} />
                  <Typography variant="body2">המלצות מותאמות אישית לחיסכון</Typography>
                </BenefitItem>
                
                <BenefitItem>
                  <AccessTimeIcon sx={{ color: "#C9B18C", fontSize: 20 }} />
                  <Typography variant="body2">תשובה מהירה תוך יום עסקים אחד</Typography>
                </BenefitItem>
              </BenefitsSection>
            </InfoCard>
          </LeftColumn>
          
          <RightColumn>
            <FormCard component="form" onSubmit={handleSubmit}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: "#2E353E", textAlign: "center" }}>
                שליחת דוח יתרות לסילוק
              </Typography>
              
              {user && (
                <WelcomeMessage>
                  <PersonIcon sx={{ color: "#896E3B", fontSize: 18, mr: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      שלום {user.first_name || "משתמש יקר"}, הטופס מולא אוטומטית
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        const filledFields = [
                          user.first_name && "שם", 
                          user.email && "אימייל", 
                          user.phone && "טלפון",
                          user.bank_id && "בנק"
                        ].filter(Boolean);
                        
                        return filledFields.length > 0 
                          ? `מילאנו עבורך: ${filledFields.join(", ")}`
                          : "אתה יכול למלא את הפרטים הנחוצים";
                      })()}
                    </Typography>
                  </Box>
                </WelcomeMessage>
              )}
              
              <FormField>
                <StyledTextField
                  select
                  label="בחר בנק"
                  name="bank"
                  value={formData.bank}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 200 }
                      }
                    }
                  }}
                  InputProps={{ 
                    endAdornment: user?.bank_id && !modifiedFields.bank ? (
                      <ProfileBadge>
                        <PersonIcon />
                        מהפרופיל
                      </ProfileBadge>
                    ) : null
                  }}
                >
                  <MenuItem value=""><em>בחר בנק</em></MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                  ))}
                </StyledTextField>
              </FormField>
              
              <FormField>
                <StyledTextField
                  label="שם מלא"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{ 
                    endAdornment: user?.first_name && !modifiedFields.fullName ? (
                      <ProfileBadge>
                        <PersonIcon />
                        מהפרופיל
                      </ProfileBadge>
                    ) : null
                  }}
                />
              </FormField>
              
              <FormField>
                <StyledTextField
                  label="אימייל"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{ 
                    endAdornment: user?.email && !modifiedFields.email ? (
                      <ProfileBadge>
                        <PersonIcon />
                        מהפרופיל
                      </ProfileBadge>
                    ) : null
                  }}
                />
              </FormField>
              
              <FormField>
                <StyledTextField
                  label="טלפון"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{ 
                    endAdornment: user?.phone && !modifiedFields.phone ? (
                      <ProfileBadge>
                        <PersonIcon />
                        מהפרופיל
                      </ProfileBadge>
                    ) : null
                  }}
                />
              </FormField>
              
              <FileUploadSection>
                <FileUploadLabel htmlFor="upload-pdf">
                  <CloudUploadIcon sx={{ fontSize: 36, mb: 1, color: "#C9B18C" }} />
                  <Typography sx={{ mb: 0.5 }}>העלאת דוח יתרות לסילוק</Typography>
                  <Typography variant="caption" color="text.secondary">
                    (PDF בלבד)
                  </Typography>
                  {fileName && (
                    <FileNameChip>
                      <Typography variant="caption" color="primary">
                        {fileName}
                      </Typography>
                    </FileNameChip>
                  )}
                </FileUploadLabel>
                <FileInput
                  id="upload-pdf"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  required
                />
              </FileUploadSection>
              
              <RegulationsSection>
                <RegulationsAccordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="regulations-content"
                    id="regulations-header"
                  >
                    <GavelIcon sx={{ fontSize: 20, color: "#896E3B", mr: 1 }} />
                    <Typography fontWeight={500}>תקנון שימוש – העלאת דוח יתרות סילוק למשכנתא</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RegulationsText>
                      <Typography variant="body2" paragraph>
                        הנך מתבקש לקרוא בעיון את התנאים שלהלן בטרם העלאת דוח יתרות סילוק למשכנתא (להלן: "הדוח") לאתר. העלאת הדוח מהווה אישור והסכמה מצדך לתנאים אלה:
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        הצהרת בעלות והסכמה להעברת מידע
                      </Typography>
                      <Typography variant="body2" paragraph>
                        המשתמש מצהיר ומאשר כי הוא הבעלים החוקי של הדוח ו/או מוסמך על ידי בעליו כדין לעשות בו שימוש, וכי מסירת המידע נעשית מרצונו החופשי, מתוך הבנה כי המידע ינותח במסגרת שירותי הייעוץ הניתנים באתר.
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        שימוש במידע
                      </Typography>
                      <Typography variant="body2" paragraph>
                        הדוח ייעשה בו שימוש לצורכי בדיקה, ניתוח ומתן ייעוץ אישי בתחום המשכנתאות והתחייבויות פיננסיות נלוות בלבד. המידע לא יועבר לכל צד שלישי, למעט אם נדרש לפי כל דין או על פי צו שיפוטי.
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        אחסון ושמירת מידע
                      </Typography>
                      <Typography variant="body2" paragraph>
                        הדוח וכל מידע שיימסר באמצעות האתר יישמרו בצורה מאובטחת במערכות מוגנות, לתקופה של עד 12 חודשים מיום העלאתם, אלא אם תתבקש הארכה מפורשת מצד המשתמש או תימסר בקשה למחיקה מוקדמת.
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        זכויות משתמש – עיון, תיקון ומחיקה
                      </Typography>
                      <Typography variant="body2" paragraph>
                        המשתמש רשאי לעיין בכל מידע שמסר, לבקש את תיקונו או מחיקתו, בהתאם להוראות חוק הגנת הפרטיות, התשמ"א–1981.
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        אחריות והגבלת אחריות
                      </Typography>
                      <Typography variant="body2" paragraph>
                        הייעוץ הניתן באתר מבוסס על הנתונים שיימסרו על-ידי המשתמש, ואינו מהווה ייעוץ משפטי, פיננסי או בנקאי מחייב. השירותים ניתנים כפי שהם (As-Is), ומשכך, אין האתר ו/או מי מטעמו אחראים לכל נזק ישיר או עקיף שייגרם כתוצאה משימוש בשירות או מהסתמכות על הייעוץ שיינתן.
                      </Typography>

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        הצהרת הסכמה
                      </Typography>
                      <Typography variant="body2">
                        מובהר בזאת, כי העלאת הדוח לאתר מהווה הסכמה מלאה ומודעת מצד המשתמש לכל תנאי התקנון המפורטים לעיל.
                      </Typography>
                    </RegulationsText>
                  </AccordionDetails>
                </RegulationsAccordion>
                
                <CheckboxContainer>
                  <StyledFormControlLabel
                    control={
                      <Checkbox 
                        checked={agreedToTerms}
                        onChange={handleTermsChange}
                        sx={{
                          color: "#C9B18C",
                          '&.Mui-checked': {
                            color: "#896E3B",
                          },
                        }}
                      />
                    }
                    label="קראתי ואני מסכים/ה לתנאי התקנון"
                  />
                </CheckboxContainer>
              </RegulationsSection>
              
              <SubmitButton
                type="submit"
                variant="contained"
                disabled={isLoading || !agreedToTerms}
                fullWidth
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLoading ? "שולח..." : "שלח את הדוח לבדיקה"}
              </SubmitButton>
            </FormCard>
            
            {isSuccess && (
              <Fade in={isSuccess}>
                <SuccessMessage severity="success" icon={<CheckCircleOutlineIcon />}>
                  {uploadMessage}
                </SuccessMessage>
              </Fade>
            )}
            
            {isError && (
              <Fade in={isError}>
                <ErrorMessage severity="error">
                  {uploadMessage}
                </ErrorMessage>
              </Fade>
            )}
          </RightColumn>
        </MainContent>
        
        <ImportantNote>
          <InfoIcon sx={{ color: "#C9B18C", fontSize: 16, mr: 1 }} />
          <Typography variant="body2">
            <strong>חשוב לדעת:</strong> השירות ניתן <strong>ללא עלות</strong>, ונחזור אליכם עם תוצאות הבדיקה תוך <strong>יום עסקים אחד</strong>.
          </Typography>
        </ImportantNote>
      </PageContainer>
    </Fade>
  );
};

// Styled Components
const PageContainer = styled(Box)`
  max-width: 1000px;
  margin: 40px auto;
  padding: 35px;
  position: relative;
  direction: rtl;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 4px;
    width: 100%;
    background: linear-gradient(to right, #C9B18C, #E9DBC8);
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
    margin: 20px 10px;
  }
`;

const ServiceBadge = styled(Box)`
  display: flex;
  align-items: center;
  background-color: #FBFAF8;
  border: 1px solid #F0EBE4;
  border-radius: 16px;
  padding: 4px 10px;
  width: fit-content;
  margin-bottom: 16px;
`;

const PageHeader = styled(Box)`
  text-align: center;
  margin-bottom: 30px;
`;

const MainContent = styled(Box)`
  display: flex;
  gap: 30px;
  
  @media (max-width: 900px) {
  flex-direction: column;
  }
`;

const LeftColumn = styled(Box)`
  flex: 1;
  
  @media (max-width: 900px) {
    order: 1;
  }
`;

const RightColumn = styled(Box)`
  flex: 1;
  
  @media (max-width: 900px) {
    order: 2;
  }
`;

const InfoCard = styled(Paper)`
  padding: 24px;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid #f0f0f0;
`;

const InfoCardHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const BenefitsSection = styled(Box)`
  margin-top: 10px;
`;

const BenefitItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const GuideButtonContainer = styled(Box)`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 900px) {
    margin-top: 25px;
    margin-bottom: 5px;
  }
`;

const GuideButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #F8F5F0;
  color: #896E3B;
  font-weight: 500;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #E9DBC8;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(201, 177, 140, 0.15);
  
  &:hover {
    background-color: #EFE8DC;
    box-shadow: 0 4px 8px rgba(201, 177, 140, 0.25);
  }
  
  @media (max-width: 900px) {
    padding: 12px 24px;
    font-size: 16px;
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
  }
`;

const ArrowIcon = styled.span`
  margin-right: 8px;
  transition: transform 0.2s ease;
  
  ${GuideButton}:hover & {
    transform: translateX(-4px);
  }
`;

const FormCard = styled(Paper)`
  padding: 28px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  background-color: #fff;
  border: 1px solid #f5f5f5;
`;

const FormField = styled(Box)`
  margin-bottom: 20px;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #C9B18C;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #C9B18C;
    }
  }
  
  & .MuiInputLabel-root.Mui-focused {
    color: #896E3B;
  }
  
  & .MuiInputBase-input {
    direction: rtl;
    text-align: right;
  }
  
  & .MuiFormLabel-root {
    right: 14px;
    left: auto;
    transform-origin: top right;
    text-align: right;
    direction: rtl;
    
    &.MuiInputLabel-shrink {
      transform: translate(0, -9px) scale(0.75);
    }
  }
  
  & .MuiSelect-icon {
    right: auto;
    left: 7px;
  }
`;


const FileUploadSection = styled(Box)`
  margin-bottom: 25px;
`;

const FileUploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #E0E0E0;
  border-radius: 8px;
  padding: 30px 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #FBFAF8;
  text-align: center;
  
  &:hover {
    border-color: #C9B18C;
    background-color: #F8F5F0;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileNameChip = styled(Box)`
  margin-top: 10px;
  padding: 4px 12px;
  background: rgba(201, 177, 140, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(201, 177, 140, 0.2);
`;

const SubmitButton = styled(Button)`
  background-color: #896E3B;
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(137, 110, 59, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #735a31;
    box-shadow: 0 6px 15px rgba(137, 110, 59, 0.25);
  }
  
  &:disabled {
    background-color: #e0e0e0;
    color: #a0a0a0;
  }
`;

const SuccessMessage = styled(Alert)`
  margin-top: 20px;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: #EAF7EE;
  color: #2E7D32;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.1);
`;

const ErrorMessage = styled(Alert)`
  margin-top: 20px;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: #FEECEB;
  color: #D32F2F;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.1);
`;

const ImportantNote = styled(Box)`
  display: flex;
  align-items: center;
  background-color: #FBFAF8;
  border: 1px solid #F0EBE4;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 40px;
`;

// New Styled Components for Regulations
const RegulationsSection = styled(Box)`
  margin-bottom: 25px;
`;

const RegulationsAccordion = styled(Accordion)`
  border: 1px solid #E9DBC8;
  border-radius: 8px !important;
  box-shadow: none;
  background-color: #FBFAF8;
  overflow: hidden;
  
  &:before {
    display: none;
  }
  
  & .MuiAccordionSummary-root {
    min-height: 50px;
    padding: 0 16px;
  }
  
  & .MuiAccordionSummary-content {
    margin: 10px 0;
  }
  
  & .MuiAccordionDetails-root {
    padding: 8px 16px 16px;
    background-color: #fff;
  }
  
  & .MuiSvgIcon-root {
    color: #896E3B;
  }
`;

const RegulationsText = styled(Box)`
  max-height: 250px;
  overflow-y: auto;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #C9B18C;
    border-radius: 8px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #896E3B;
  }
`;

const CheckboxContainer = styled(Box)`
  margin-top: 12px;
  padding: 0 8px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  & .MuiFormControlLabel-label {
    font-size: 0.9rem;
    color: #2E353E;
  }
`;

const ProfileBadge = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: rgba(201, 177, 140, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(201, 177, 140, 0.2);
  font-size: 0.75rem;
  color: #896E3B;
  margin-left: 8px;
`;

const MortgageAlert = styled(Box)`
  display: flex;
  align-items: flex-start;
  background-color: rgba(201, 177, 140, 0.1);
  border: 1px solid #E9DBC8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(201, 177, 140, 0.1);
`;

const WelcomeMessage = styled(Box)`
  display: flex;
  align-items: flex-start;
  background-color: rgba(201, 177, 140, 0.08);
  border: 1px solid #E9DBC8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

export default UploadPage;
